import { createSlice } from "@reduxjs/toolkit";

import { RootState } from "@/redux";

import { OutreachInstructionTemplate } from "../../pages/List/schema/outreach-instruction-template.schema";

import { listOutreachTemplateApi, OutreachTemplateScopeArgs } from "./list-outreach-template.api";

/**
 * The cache key for one shelf of the library.
 *
 * `null` and the empty string must not collide with a real source type, and they cannot: no
 * `ListSourceType` is empty, so `"SENDER_ROUTING:"` is unambiguously the company-wide shelf.
 */
export const outreachTemplateScopeKey = ({
	kind,
	listSourceType,
}: OutreachTemplateScopeArgs): string => `${kind}:${listSourceType ?? ""}`;

export interface ListOutreachTemplateState {
	/**
	 * The fetched templates per (`kind`, `listSourceType`) scope.
	 *
	 * Bucketed by scope rather than held as one "current library" array: RTK Query serves a warm
	 * cache entry **without** re-dispatching `fulfilled`, so switching back to a library that is
	 * still cached would leave a single bucket showing whichever scope was fetched last — the
	 * sender-rule templates would show up under a list's copywriting picker (apps/web CLAUDE.md §2).
	 */
	templatesByScope: Record<string, OutreachInstructionTemplate[]>;
}

const initialState: ListOutreachTemplateState = {
	templatesByScope: {},
};

export const listOutreachTemplateSlice = createSlice({
	name: "listOutreachTemplate",
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		// Filed under the scope the request was MADE for (`meta.arg.originalArgs`), never under
		// whichever library is currently open: a response can land after the user has switched.
		builder.addMatcher(
			listOutreachTemplateApi.endpoints.getOutreachInstructionTemplates.matchFulfilled,
			(state, { payload, meta }) => {
				state.templatesByScope[outreachTemplateScopeKey(meta.arg.originalArgs)] = payload;
			}
		);
	},
});

export const listOutreachTemplateReducer = listOutreachTemplateSlice.reducer;

export const listOutreachTemplateSelector = (state: RootState): ListOutreachTemplateState =>
	state[listOutreachTemplateSlice.name];

/**
 * One scope's templates as the slice holds them — `undefined` until that scope has been fetched
 * once, so a caller can tell "not loaded yet" from "the company has none".
 */
export const outreachTemplatesSelector =
	(scope: OutreachTemplateScopeArgs | undefined) =>
	(state: RootState): OutreachInstructionTemplate[] | undefined =>
		scope === undefined
			? undefined
			: state[listOutreachTemplateSlice.name].templatesByScope[outreachTemplateScopeKey(scope)];

/** The scope's company default, if it has one — the row the Templates dropdown badges. */
export const defaultOutreachTemplateSelector =
	(scope: OutreachTemplateScopeArgs | undefined) =>
	(state: RootState): OutreachInstructionTemplate | undefined =>
		outreachTemplatesSelector(scope)(state)?.find((template) => template.isDefault);
